import { ConfigProvider, Spin } from "antd";
import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import {
  createContext,
  Suspense,
  useEffect,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { useMediaQuery } from "react-responsive";
import {
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";
import { PersistGate } from "redux-persist/integration/react";

import RouteGuard from "./core/guard/route.guard";
import { persistor, store } from "./core/store";
import { setScreenType } from "./core/store/reducers/settings.slice";
import { WEB_URL } from "./shared/constants";
import { msalConfig } from "./msalConfig";
import { Provider } from "react-redux";

const msalInstance = new PublicClientApplication(msalConfig);

/**
 * antd v5 reads its palette from these tokens, not from the `@primary-color`
 * Less variable (that only worked in v4). Without this, every antd component
 * falls back to the default blue #1677ff, which is why primary buttons outside
 * `.app-page` looked blue while the CSS-overridden ones looked indigo.
 *
 * Values mirror the tokens in assets/styles/theme.less — keep them in sync.
 */
const themeConfig = {
  token: {
    colorPrimary: "#6366f1",
    colorLink: "#6366f1",
    colorSuccess: "#059669",
    colorWarning: "#d97706",
    colorError: "#dc2626",
    colorInfo: "#2563eb",
  },
  components: {
    Button: {
      colorPrimaryHover: "#4f46e5",
      colorPrimaryActive: "#4338ca",
    },
  },
};

type BlockerFunction = (location: { pathname: string; search: string }) => boolean;

interface NavigationBlockerContextType {
  blocker: BlockerFunction | null;
  setBlocker: (blocker: BlockerFunction | null) => void;
}

export const NavigationBlockerContext =
  createContext<NavigationBlockerContextType>({
    blocker: null,
    setBlocker: () => { },
  });

function App() {
  useEffect(() => {
    const script = document.createElement("script");

    script.src = WEB_URL + "ckfinder/ckfinder.js";
    script.async = true;

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <ConfigProvider theme={themeConfig}>
          <Router>
            <AppRouted />
          </Router>
        </ConfigProvider>
      </PersistGate>
    </Provider>
  );
}

export default App;

export function AppRouted() {
  const dispatch = useDispatch();

  const [blocker, setBlocker] = useState<BlockerFunction | null>(null);

  const screenType = {
    isXl: useMediaQuery({ minWidth: 1201 }),
    isLg: useMediaQuery({ minWidth: 992, maxWidth: 1200 }),
    isMd: useMediaQuery({ minWidth: 768, maxWidth: 991 }),
    isSm: useMediaQuery({ minWidth: 576, maxWidth: 767 }),
    isXs: useMediaQuery({ maxWidth: 575 }),
    isPortrait: useMediaQuery({
      query: "(orientation: portrait)",
    }),
  };

  useEffect(() => {
    dispatch(setScreenType(screenType));
  }, []);

  return (
    <NavigationBlockerContext.Provider
      value={{ blocker, setBlocker }}
    >
      <Suspense
        fallback={
          <div>
            <Spin />
          </div>
        }
      >
        <MsalProvider instance={msalInstance}>
          <Routes>
            <Route path="*" element={<RouteGuard />} />
          </Routes>
        </MsalProvider>
      </Suspense>
    </NavigationBlockerContext.Provider>
  );
}